import { shallowRef } from 'vue';
import { summarizeSchedule, summaryText, type ScheduleRow } from '@/utils/customPlaylistSchedule';

/**
 * The nested schedule items on a custom list's search document, and the
 * schedule writes this browser made that the document may not show yet.
 *
 * Why the pending writes exist. On staging and production the list document is
 * reindexed by a queued job, so for a while after a save every copy of the list
 * that arrives — a search page, the Search page's 30-second live poll, the
 * inspector's record, or the row object a drawer was opened with — is the OLD
 * document. Patching the TanStack cache once is not enough: the next poll tick
 * replaces the patched row, and a drawer or inspector tab that is remounted
 * starts again from its snapshot. So every save and delete is also recorded
 * here, module-wide and keyed by list id, and every place that shows a list's
 * schedules reads its items through {@link withPendingScheduleWrites}.
 *
 * Applying a pending write is idempotent. Once the reindexed document arrives it
 * already holds the saved values, so the overlay changes nothing. Writes are
 * kept for the session; see {@link PendingWrite} for why they no longer age out.
 */

/** The nested key on the list document that holds its schedules. */
export const NESTED_SCHEDULES_FIELD = 'location_zone_custom_playlist_schedules';

/**
 * The keys a nested schedule item carries in the list document (contract C1).
 * A patched item keeps to them so it looks like one the index wrote.
 */
export const NESTED_SCHEDULE_KEYS = [
    'id',
    'display_string',
    'location_zone_id',
    'custom_playlist_schedule_type_id',
    'custom_playlist_insert_type_id',
    'silence',
    'date',
    'end_date',
    'time',
    'end_time',
    'recurring_start_date',
    'recurring_end_date',
    'recurring_weekly_interval',
    'delimited_day_ids',
    'messaging_start_time',
    'messaging_end_time',
    'messaging_frequency_minutes',
    'insert_frequency_tracks_start',
    'insert_frequency_tracks_target',
    'insert_frequency_ramp_days',
    'station_change_location_zone_player_id',
    'priority',
    'effective_start_datetime',
    'effective_end_datetime',
    'loop',
    'should_shuffle',
    'resume_from_file_offset',
    'complete_track',
    'ignore_dl_window',
    'revert_to_daily',
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function scheduleItems(value: unknown): ScheduleRow[] {
    return Array.isArray(value) ? (value.filter(isRecord) as ScheduleRow[]) : [];
}

/**
 * Turn an API row into a nested item: C1's keys only, with display_string
 * recomputed, because the stored one described the schedule before the edit.
 */
export function toNestedScheduleItem(row: Record<string, unknown>, now: Date = new Date()): ScheduleRow {
    const item: Record<string, unknown> = {};
    for (const key of NESTED_SCHEDULE_KEYS) {
        if (key in row) {
            item[key] = row[key];
        }
    }
    item.display_string = summaryText(summarizeSchedule(item as ScheduleRow, { now }));
    return item as ScheduleRow;
}

/**
 * Put a saved row into a list's nested schedules: it replaces the item with
 * the same id (keeping any key the response left out, since a create's
 * response echoes only what was sent) or is appended.
 */
export function upsertScheduleItem(items: unknown, saved: Record<string, unknown>, now: Date = new Date()): ScheduleRow[] {
    const current = scheduleItems(items);
    const savedId = Number(saved.id);
    const index = current.findIndex((item) => Number(item.id) === savedId);
    if (index === -1) {
        return [...current, toNestedScheduleItem(saved, now)];
    }
    const existing = current[index] as Record<string, unknown>;
    const merged = toNestedScheduleItem({ ...existing, ...saved, id: existing.id }, now);
    return current.map((item, position) => (position === index ? merged : item));
}

export function removeScheduleItem(items: unknown, scheduleId: number): ScheduleRow[] {
    return scheduleItems(items).filter((item) => Number(item.id) !== scheduleId);
}

/**
 * A write is kept for the rest of the session: until logout, or a reload.
 *
 * It used to age out after two minutes. But a drawer or an inspector opened
 * from a row keeps that row until it closes, and never receives a fresh copy.
 * Once the write aged out, the drawer showed the old schedule again. A deleted
 * row came back, a created one vanished, and editing the reverted row saved
 * its old days and times over the edit on live programming. Applying a write
 * is idempotent, so keeping it costs nothing once the reindexed document
 * arrives. The trade-off: if someone else later edits a schedule this browser
 * saved, this browser shows its own version until it reloads.
 */
interface PendingWrite {
    /** The saved row as the API returned it, or null for a delete. */
    saved: Record<string, unknown> | null;
}

/** listId -> scheduleId -> the write, in the order the writes were made. */
type PendingByList = Map<number, Map<number, PendingWrite>>;

/** Replaced (never mutated) on every write, so a computed that read it re-runs. */
const pendingWrites = shallowRef<PendingByList>(new Map());

function positiveId(value: unknown): number | null {
    const id = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
    return typeof id === 'number' && Number.isInteger(id) && id > 0 ? id : null;
}

function record(listId: number, scheduleId: number, saved: Record<string, unknown> | null): void {
    const next: PendingByList = new Map(pendingWrites.value);
    const writes = new Map(next.get(listId) ?? []);
    // Re-inserted so the latest write is applied last.
    writes.delete(scheduleId);
    writes.set(scheduleId, { saved });
    next.set(listId, writes);
    pendingWrites.value = next;
}

/** Remember a created or edited schedule on its list. */
export function recordScheduleSaved(listId: number, saved: Record<string, unknown>): void {
    const scheduleId = positiveId(saved.id);
    if (scheduleId === null) return;
    record(listId, scheduleId, saved);
}

/** Remember that a schedule is gone from its list. */
export function recordScheduleDeleted(listId: number, scheduleId: number): void {
    record(listId, scheduleId, null);
}

/**
 * Rewrite this browser's own saves of the given schedules, on every list. A bulk edit calls it
 * so a schedule saved on its own earlier is not drawn with its old values over the bulk change.
 * A schedule with no save, or a deleted one, is left as it is.
 */
export function patchPendingScheduleSaves(
    scheduleIds: ReadonlySet<number>,
    patch: (saved: Record<string, unknown>) => Record<string, unknown>,
): void {
    let next: PendingByList | null = null;
    for (const [listId, writes] of pendingWrites.value) {
        for (const [scheduleId, write] of writes) {
            if (!scheduleIds.has(scheduleId) || write.saved === null) continue;
            next ??= new Map(pendingWrites.value);
            const listWrites = new Map(next.get(listId)!);
            // Set in place, so the write keeps its order among the list's writes.
            listWrites.set(scheduleId, { saved: patch(write.saved) });
            next.set(listId, listWrites);
        }
    }
    if (next) pendingWrites.value = next;
}

/**
 * A list's schedules with this browser's recent writes applied on top. Returns
 * `items` itself when the list has no pending write, so an untouched list
 * keeps its identity and nothing downstream recomputes for it.
 */
export function withPendingScheduleWrites(listId: unknown, items: unknown, now: number = Date.now()): unknown {
    const id = positiveId(listId);
    const writes = id === null ? undefined : pendingWrites.value.get(id);
    if (!writes || writes.size === 0) return items;
    let result = scheduleItems(items);
    for (const [scheduleId, write] of writes) {
        result = write.saved === null ? removeScheduleItem(result, scheduleId) : upsertScheduleItem(result, write.saved, new Date(now));
    }
    return result;
}

/** Forget every pending write. Called on logout and by tests. */
export function resetPendingScheduleWrites(): void {
    pendingWrites.value = new Map();
}
